import React, { useState, useMemo } from 'react';
import { Play, ArrowRight, Search, X, SlidersHorizontal, Star } from 'lucide-react';
import { G1, GG, NAVY, OR, OG, HeroGlow } from '../../config/styles';
import { Spin, Overlay, Modal, SmartIntro } from '../shared';
import Instructors from './Instructors';

const PURPLE_LINK = '#667eea';

export default function Academy({ courses, enrollments, lessons, categories = [], instructors = [], reviews = [], customer, lang, isRTL, loading, t, onSetPage, onOpenInstructor, onOpenPayModal, onOpenCustModal }) {
  const isEnrolled = (cid) => enrollments.some(e => e.customer_id === customer?.id && e.course_id === cid && e.status === 'active');
  const cLessons   = (cid) => lessons.filter(l => l.course_id === cid).sort((a, b) => a.order_num - b.order_num);
  const ar = lang === 'ar';
  const [previewCourse, setPreviewCourse] = useState(null);

  // تقييم الكورس = متوسط الآراء المعتمدة (بدون عدد المقيّمين)
  const courseRating = (cid) => {
    const rs = reviews.filter(r => r.course_id === cid && r.is_approved === true);
    if (!rs.length) return 0;
    return rs.reduce((s, r) => s + (Number(r.rating) || 0), 0) / rs.length;
  };

  // اسم المدرب (من جدول المدربين أو النص القديم)
  const instructorName = (c) => {
    const inst = instructors.find(i => i.id === c.instructor_id);
    return inst ? inst.name_ar : (c.instructor_name || '');
  };

  // ── البحث والفلاتر ──────────────────────────────────────
  const [query,    setQuery]    = useState('');
  const [view, setView] = useState('courses');   // courses | instructors
  const [focusInst, setFocusInst] = useState(null);
  const [activeCat, setActiveCat] = useState('all');   // all | category id
  const [priceType, setPriceType] = useState('all');   // all | free | paid
  const [priceOp,  setPriceOp]  = useState('');         // '' | gt | lt | eq
  const [priceVal, setPriceVal] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return courses.filter(c => {
      // التصنيف
      if (activeCat !== 'all' && c.category_id !== activeCat) return false;
      // بحث نصي في اسم الكورس واسم المدرب (الجديد أو القديم) — بجزء من الكلمة
      if (q) {
        const inst = instructors.find(i => i.id === c.instructor_id);
        const hay = `${c.name_ar || ''} ${c.name_en || ''} ${c.instructor_name || ''} ${inst?.name_ar || ''} ${inst?.name_en || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const price = Number(c.price) || 0;
      // مجاني / مدفوع
      if (priceType === 'free' && price !== 0) return false;
      if (priceType === 'paid' && price === 0) return false;
      // مقارنة بالسعر
      if (priceOp && priceVal !== '') {
        const v = Number(priceVal);
        if (priceOp === 'gt' && !(price >  v)) return false;
        if (priceOp === 'lt' && !(price <  v)) return false;
        if (priceOp === 'eq' && !(price === v)) return false;
      }
      return true;
    });
  }, [courses, instructors, query, activeCat, priceType, priceOp, priceVal]);

  // هل في فلترة شغّالة؟ (لو آه نعرض شبكة واحدة، لو لأ نعرض أقسام)
  const isFiltering = query || activeCat !== 'all' || priceType !== 'all' || priceOp;

  // الكورسات مجمّعة حسب التصنيف (للعرض بأقسام)
  const grouped = useMemo(() => {
    const sortedCats = [...categories].filter(c => c.is_active !== false).sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
    const groups = sortedCats.map(cat => ({
      cat,
      items: filtered.filter(c => c.category_id === cat.id),
    })).filter(g => g.items.length > 0);
    // كورسات من غير تصنيف
    const noCat = filtered.filter(c => !c.category_id || !sortedCats.find(cat => cat.id === c.category_id));
    if (noCat.length) groups.push({ cat: { id: 'none', name_ar: 'كورسات أخرى', name_en: 'Other', icon: '📦' }, items: noCat });
    return groups;
  }, [filtered, categories]);

  const clearAll = () => { setQuery(''); setActiveCat('all'); setPriceType('all'); setPriceOp(''); setPriceVal(''); };
  const hasActive = query || activeCat !== 'all' || priceType !== 'all' || priceOp;

  // ── كارت الكورس (مُعاد استخدامه في الأقسام والشبكة) ──
  const renderCard = (c, index = 0) => {
    const enrolled = isEnrolled(c.id);
    const cl = cLessons(c.id);
    const instName = instructorName(c);
    return (
      <div key={c.id} style={{ animation: 'cardRise .55s cubic-bezier(.22,1,.36,1) both', animationDelay: `${(index % 12) * 0.07}s` }}>
        <div className="gw-card" style={{ background: 'rgba(255,255,255,0.045)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.28)' }}>
          <div style={{ position: 'relative', overflow: 'hidden', minHeight: '140px' }}>
            {c.thumbnail_url ? (
              <img src={c.thumbnail_url} alt={c.name_ar}
                style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }} />
            ) : (
              <div style={{ background: OG, padding: '28px', textAlign: 'center' }}>
                <div style={{ position: 'absolute', top: '-20px', [isRTL ? 'left' : 'right']: '-20px', fontSize: '80px', opacity: 0.12, transform: 'rotate(-15deg)' }}>{c.icon}</div>
                <div style={{ fontSize: '52px', margin: '0 0 8px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}>{c.icon}</div>
              </div>
            )}
            <div style={{ position: 'absolute', top: '10px', [isRTL ? 'left' : 'right']: '10px', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', borderRadius: '8px', padding: '3px 10px', fontSize: '11px', color: 'white', fontWeight: 'bold' }}>{cl.length} {t.lessons}</div>
            {enrolled && <div style={{ position: 'absolute', top: '10px', [isRTL ? 'right' : 'left']: '10px', background: '#10b981', borderRadius: '8px', padding: '3px 10px', fontSize: '10px', color: 'white', fontWeight: 'bold' }}>✓ {t.enrolled}</div>}
            {c.thumbnail_url && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75), transparent)', padding: '20px 14px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '20px' }}>{c.icon}</span>
                  <h3 style={{ color: 'white', fontSize: '14px', fontWeight: 'bold', margin: 0, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{lang === 'ar' ? c.name_ar : c.name_en}</h3>
                </div>
              </div>
            )}
            {!c.thumbnail_url && (
              <div style={{ background: OG, textAlign: 'center', paddingBottom: '16px' }}>
                <h3 style={{ color: 'white', fontSize: '17px', fontWeight: 'bold', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{lang === 'ar' ? c.name_ar : c.name_en}</h3>
              </div>
            )}
          </div>
          <div style={{ padding: '18px' }}>
            {/* اسم المدرب (قابل للضغط) */}
            {instName && (
              <div
                onClick={(e) => { e.stopPropagation(); const inst = instructors.find(i => i.id === c.instructor_id); if (inst) { setFocusInst(inst.id); setView('instructors'); window.scrollTo(0, 0); } }}
                style={{ fontSize: '12px', color: c.instructor_id ? '#fb923c' : '#94a3b8', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px', cursor: c.instructor_id ? 'pointer' : 'default', fontWeight: c.instructor_id ? 'bold' : 'normal' }}>
                👨‍🏫 {instName}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#ffffff' }}>
                {Number(c.price) === 0 ? (t.free) : <>{c.price} <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#94a3b8' }}>{t.egpLabel}</span></>}
              </div>
              {/* التقييم — رقم + نجوم (بدون عدد المقيّمين) */}
              {(() => {
                const rt = courseRating(c.id);
                return rt > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(251,191,36,0.13)', padding: '4px 10px', borderRadius: '14px', border: '1px solid rgba(251,191,36,0.3)' }}>
                    <b style={{ fontSize: '14px', color: '#fcd34d' }}>{rt.toFixed(1)}</b>
                    <span style={{ display: 'flex', gap: '1px' }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} size={12} fill={n <= Math.round(rt) ? '#fbbf24' : 'none'} color={n <= Math.round(rt) ? '#fbbf24' : '#78716c'} />
                      ))}
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: '11px', color: '#fb923c', fontWeight: 'bold', background: 'rgba(249,115,22,0.13)', padding: '4px 10px', borderRadius: '14px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid rgba(249,115,22,0.25)' }}>
                    <Star size={11} color="#fb923c" /> {t.newBadge}
                  </div>
                );
              })()}
            </div>
            {enrolled ? (
              <button onClick={() => onSetPage('player', c, cl[0] || null)}
                style={{ width: '100%', padding: '11px', background: GG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: '0 4px 12px rgba(16,185,129,0.35)' }}>
                <Play size={14} /> {t.watchNow}
              </button>
            ) : customer?.isGuest ? (
              <button onClick={() => onOpenCustModal('register')}
                style={{ width: '100%', padding: '11px', background: OG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(249,115,22,0.4)' }}>
                {t.registerBtn + ' للاشتراك'}
              </button>
            ) : (
              <button onClick={() => {
                if (!customer) { onOpenCustModal('login'); return; }
                onOpenPayModal(c);
              }}
                style={{ width: '100%', padding: '11px', background: OG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 12px rgba(249,115,22,0.4)' }}>
                {t.enrollNow}
              </button>
            )}
            {c.drive_intro_id && (
              <button onClick={() => setPreviewCourse(c)}
                style={{ width: '100%', marginTop: '8px', padding: '10px', background: 'transparent', color: '#fb923c', border: '1.5px solid rgba(251,146,60,0.45)', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Play size={13} /> {t.freePreview}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', isolation: 'isolate', fontFamily: "'Tajawal', sans-serif" }}>
      {/* خلفية الأكاديمية الكحلي — تمتد على عرض الشاشة بأمان */}
      <div aria-hidden style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', marginLeft: '-50vw', width: '100vw', background: NAVY, zIndex: -1, overflow: 'hidden', pointerEvents: 'none' }}>
        <div className="gw-orb" style={{ width: '320px', height: '320px', background: '#f97316', top: '-90px', right: '-60px' }} />
        <div className="gw-orb" style={{ width: '300px', height: '300px', background: '#6366f1', bottom: '-70px', left: '-50px', animationDelay: '-6s' }} />
      </div>
      <HeroGlow />

      <div style={{ textAlign: 'center', color: 'white', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '40px', fontWeight: 'bold', textShadow: '2px 2px 12px rgba(0,0,0,0.2)' }}>🎓 {t.academy}</h2>
        <p style={{ opacity: 0.85, fontSize: '15px', marginTop: '6px' }}>
          {view === 'courses' ? <>{filtered.length} {t.coursesAvailable}</> : <>{instructors.filter(i => i.is_active !== false).length} {t.instructorsCount}</>}
        </p>
      </div>

      {/* ── تبويب: الكورسات / المدربون ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '26px' }}>
        {[
          { id: 'courses',     label: '📚 ' + t.coursesLabel },
          { id: 'instructors', label: '👨‍🏫 ' + t.instructorsTitle },
        ].map(v => (
          <button key={v.id} onClick={() => { setView(v.id); setFocusInst(null); }}
            style={{ padding: '10px 24px', borderRadius: '24px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', background: view === v.id ? 'white' : 'rgba(255,255,255,0.18)', color: view === v.id ? OR : 'white', backdropFilter: 'blur(6px)', boxShadow: view === v.id ? '0 4px 14px rgba(0,0,0,0.18)' : 'none', transition: 'all .2s' }}>
            {v.label}
          </button>
        ))}
      </div>

      {/* ═══ عرض المدربين جوا الأكاديمية ═══ */}
      {view === 'instructors' ? (
        <Instructors
          instructors={instructors} courses={courses} lessons={lessons}
          enrollments={enrollments} customer={customer}
          lang={lang} isRTL={isRTL} loading={loading} t={t}
          focusInstructorId={focusInst}
          clearFocus={() => setFocusInst(null)}
          onSetPage={onSetPage}
          embedded
        />
      ) : (
      <>

      {/* ── شريط البحث والفلاتر ── */}
      <div style={{ maxWidth: '720px', margin: '0 auto 28px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {/* خانة البحث */}
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={18} color="#9CA3AF" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', insetInlineStart: '14px', pointerEvents: 'none' }} />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t.searchCourse}
              style={{ width: '100%', padding: '13px 44px', borderRadius: '12px', border: 'none', fontSize: '14px', outline: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', boxSizing: 'border-box' }} />
            {query && (
              <button onClick={() => setQuery('')} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', insetInlineEnd: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex' }}>
                <X size={16} />
              </button>
            )}
          </div>
          {/* زر إظهار الفلاتر */}
          <button onClick={() => setShowFilters(s => !s)}
            style={{ padding: '13px 16px', borderRadius: '12px', border: 'none', background: showFilters ? OG : 'rgba(255,255,255,0.95)', color: showFilters ? 'white' : OR, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', whiteSpace: 'nowrap' }}>
            <SlidersHorizontal size={16} /> {t.filtersLabel}
          </button>
        </div>

        {/* لوحة الفلاتر */}
        {showFilters && (
          <div style={{ background: 'white', borderRadius: '14px', padding: '16px', marginTop: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
            {/* النوع */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', marginBottom: '6px' }}>{t.typeLabel}</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { id: 'all',  label: t.allLabel },
                  { id: 'free', label: t.free },
                  { id: 'paid', label: t.paidLabel },
                ].map(o => (
                  <button key={o.id} onClick={() => setPriceType(o.id)}
                    style={{ padding: '7px 14px', borderRadius: '8px', border: `1.5px solid ${priceType === o.id ? OR : '#E5E7EB'}`, background: priceType === o.id ? '#FFF7ED' : 'white', color: priceType === o.id ? OR : '#6B7280', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            {/* السعر */}
            <div>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', marginBottom: '6px' }}>{t.price}</div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select value={priceOp} onChange={e => setPriceOp(e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '12px', cursor: 'pointer', outline: 'none' }}>
                  <option value="">{t.anyLabel}</option>
                  <option value="gt">{t.greaterThan}</option>
                  <option value="lt">{t.lessThan}</option>
                  <option value="eq">{t.equalsLabel}</option>
                </select>
                <input type="number" value={priceVal} onChange={e => setPriceVal(e.target.value)} placeholder="0" disabled={!priceOp}
                  style={{ width: '90px', padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #E5E7EB', fontSize: '12px', outline: 'none', opacity: priceOp ? 1 : 0.5 }} />
                <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{t.egpLabel}</span>
              </div>
            </div>
            {/* مسح */}
            {hasActive && (
              <button onClick={clearAll}
                style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#FEE2E2', color: '#EF4444', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <X size={14} /> {t.clearFilters}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── تبويبات التصنيفات ── */}
      {categories.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '28px' }}>
          <button onClick={() => setActiveCat('all')}
            style={{ padding: '9px 18px', borderRadius: '24px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', background: activeCat === 'all' ? 'white' : 'rgba(255,255,255,0.18)', color: activeCat === 'all' ? OR : 'white', backdropFilter: 'blur(6px)', boxShadow: activeCat === 'all' ? '0 4px 14px rgba(0,0,0,0.15)' : 'none', transition: 'all .2s' }}>
            🎓 {t.allLabel}
          </button>
          {[...categories].filter(c => c.is_active !== false).sort((a, b) => (a.order_num || 0) - (b.order_num || 0)).map(cat => (
            <button key={cat.id} onClick={() => setActiveCat(cat.id)}
              style={{ padding: '9px 18px', borderRadius: '24px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', background: activeCat === cat.id ? 'white' : 'rgba(255,255,255,0.18)', color: activeCat === cat.id ? OR : 'white', backdropFilter: 'blur(6px)', boxShadow: activeCat === cat.id ? '0 4px 14px rgba(0,0,0,0.15)' : 'none', transition: 'all .2s' }}>
              {cat.icon} {ar ? cat.name_ar : (cat.name_en || cat.name_ar)}
            </button>
          ))}
        </div>
      )}

      {loading ? <Spin /> : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'white' }}>
          <div style={{ fontSize: '54px', marginBottom: '12px', opacity: 0.6 }}>🔍</div>
          <div style={{ fontSize: '17px', fontWeight: 'bold', marginBottom: '6px' }}>{t.noMatchingCourses}</div>
          <div style={{ fontSize: '13px', opacity: 0.8 }}>{t.tryChangeSearch}</div>
          {hasActive && (
            <button onClick={clearAll}
              style={{ marginTop: '16px', padding: '9px 20px', borderRadius: '10px', border: 'none', background: 'rgba(255,255,255,0.95)', color: OR, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              {t.clearFilters}
            </button>
          )}
        </div>
      ) : isFiltering ? (
        /* وضع الفلترة/البحث → شبكة واحدة */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))', gap: '20px' }}>
          {filtered.map((c, i) => renderCard(c, i))}
        </div>
      ) : (
        /* الوضع العادي → أقسام (كل تصنيف وتحته كورساته) */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '36px' }}>
          {grouped.map(({ cat, items }) => (
            <div key={cat.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', color: 'white' }}>
                <span style={{ fontSize: '26px' }}>{cat.icon || '📚'}</span>
                <h3 style={{ fontSize: '23px', fontWeight: 'bold', margin: 0, textShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>{ar ? cat.name_ar : (cat.name_en || cat.name_ar)}</h3>
                <span style={{ fontSize: '13px', opacity: 0.8 }}>({items.length})</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.25)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(270px,1fr))', gap: '20px' }}>
                {items.map((c, i) => renderCard(c, i))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button onClick={() => onSetPage('home')} style={{ padding: '11px 28px', background: 'rgba(255,255,255,0.95)', color: OR, border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>{t.backHome}</button>
      </div>
      </>
      )}

      {previewCourse && (
        <Overlay onClose={() => setPreviewCourse(null)} dark>
          <Modal dark maxWidth="780px">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px' }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {t.freePreview} — {ar ? previewCourse.name_ar : previewCourse.name_en}
              </h3>
              <button onClick={() => setPreviewCourse(null)}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', width: '34px', height: '34px', color: 'white', cursor: 'pointer', fontSize: '20px', lineHeight: 1, flexShrink: 0 }}>×</button>
            </div>
            <SmartIntro src={previewCourse.drive_intro_id} />
          </Modal>
        </Overlay>
      )}
    </div>
  );
}